import { useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { QueryKeys } from '@/core/models/enum'
import { InstalledSensorStatus } from '@/core/models/types'
import { sensorAdditionalDataAtom } from '@/core/store/globalStore'
import { getTagDetails } from '@/api/apiService'
import { Sensor } from '../models'

export function useSensorTagDetails(selectedSensor: Sensor | null) {
    const queryClient = useQueryClient()
    const [, setSensorAdditionalData] = useAtom(sensorAdditionalDataAtom)

    const plant = selectedSensor?.plant ?? ''
    const tag = selectedSensor?.tag ?? ''
    const isOnboardedStatus =
        selectedSensor?.status != null &&
        (selectedSensor.status === InstalledSensorStatus.Onboarded ||
            selectedSensor.status === InstalledSensorStatus.Active ||
            selectedSensor.status === InstalledSensorStatus.Offline ||
            selectedSensor.status === InstalledSensorStatus.Prepared)
    const enabled = isOnboardedStatus && !!plant && !!tag

    const queryKey = useMemo(() => [QueryKeys.TagDetails, plant, tag] as const, [plant, tag])

    useEffect(() => {
        setSensorAdditionalData([])
    }, [tag, setSensorAdditionalData])

    const query = useQuery({
        queryKey,
        enabled,
        queryFn: () => getTagDetails(plant, [tag]),
    })

    useEffect(() => {
        if (query.data) setSensorAdditionalData(query.data)
    }, [query.data, setSensorAdditionalData])

    const invalidate = () => queryClient.invalidateQueries({ queryKey })

    return {
        ...query,
        queryKey,
        invalidate,
    }
}
